import { useMemo } from "react";
import { Spin } from "antd";
import { RightOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useSetRecoilState } from "recoil";
import { dspProductAtom, productKeyAtom } from "src/atoms";
import { ProductKey, isTest, productInfo } from "src/config/config";
import fedIcon from "src/assets/images/fed_icon.svg";
import { useCreatedServiceList } from "src/hooks/api/cloud/useCreatedServiceList";
import { useRouter } from "src/hooks/useRouter";
import useProductInfo, {
  isDspProductList,
  productNameMap,
} from "src/mocks/useProductInfo";
import { setProductToSessionStorage } from "src/utils/storage/auth";
import styles from "./index.module.scss";

// 고객 문의 채팅 (inbound로 호출한 포탈 도메인을 넘긴다)
const CUSTOMER_SERVICE_URL = `https://chat.fasoo.com/wrapmsgr/mobile/customerService/cloud-cs?inbound=${
  isTest ? "test.cloudportal.fasoo.com" : "cloudportal.fasoo.com"
}`;

// 로그인 사용자가 보유(생성)한 클라우드 서비스의 제품을 고르는 화면.
// 선택 시 Home의 제품 선택과 동일하게 세션/아톰을 세팅하고 콘솔로 이동한다.
const MyServiceList = () => {
  const { createdServiceList, isLoadingCreatedServiceList } =
    useCreatedServiceList({});
  const { getProductInfo } = useProductInfo();
  const setProductKey = useSetRecoilState(productKeyAtom);
  const setDspProduct = useSetRecoilState(dspProductAtom);
  const { goConsole } = useRouter();
  const { t } = useTranslation();

  // 같은 제품의 서비스가 여러 개일 수 있어 제품 기준으로 중복 제거
  const productList = useMemo(
    () =>
      Array.from(
        new Set((createdServiceList ?? []).map((service) => service.product)),
      ),
    [createdServiceList],
  );

  const selectProduct = (product: ProductKey) => {
    setProductToSessionStorage(
      isDspProductList.includes(product) ? "DSP" : product,
    );
    setProductKey(product);
    setDspProduct(product);
    goConsole();
  };

  const goInquiry = () => {
    const width = 540;
    const height = 800;
    // screenX/outerWidth 기준이라 멀티 모니터에서도 현재 창이 있는 화면 가운데에 뜬다
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    window.open(
      CUSTOMER_SERVICE_URL,
      "cloudCustomerService",
      `width=${width},height=${height},left=${left},top=${top}`,
    );
  };

  return (
    <div className={styles.page}>
      <div className={styles.hero}>
        <div className={styles.heroInner}>
          <h2 className={styles.heroTitle}>{t("myService.title")}</h2>
          <p className={styles.heroSubTitle}>{t("myService.subTitle")}</p>
          <button
            type="button"
            className={styles.inquiryBtn}
            onClick={goInquiry}
          >
            {t("myService.support.btn")}
          </button>
        </div>
      </div>

      <div className={styles.list}>
        {isLoadingCreatedServiceList ? (
          <div className={styles.loading}>
            <Spin />
          </div>
        ) : productList.length === 0 ? (
          <p className={styles.empty}>{t("myService.empty")}</p>
        ) : (
          productList.map((product) => {
            const info = getProductInfo(product);
            // DSP는 개별 제품이 아닌 묶음이라 productsInfoMap에 없다. FED 아이콘으로 대표.
            const icon =
              info?.icon ?? (product === "DSP" ? fedIcon : undefined);
            return (
              <button
                key={product}
                type="button"
                className={styles.card}
                onClick={() => selectProduct(product)}
              >
                <div className={styles.cardTop}>
                  <div className={styles.iconBox}>
                    {icon && <img src={icon} alt="" />}
                  </div>
                  <span className={styles.detail}>
                    {t("myService.detail")}
                    <RightOutlined className={styles.chevron} />
                  </span>
                </div>
                <p className={styles.name}>
                  {/* DSP처럼 productsInfoMap에 항목이 없는 제품은 config 표시명으로 폴백 */}
                  {info?.name ??
                    productNameMap[product] ??
                    productInfo[product]?.name ??
                    product}
                </p>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

export default MyServiceList;
